/**
 * Chatbot API Service
 *
 * API methods for all chatbot endpoints: messaging (SSE), sessions, preferences,
 * slash commands, and file uploads. The AI service runs on a separate port
 * from the Java backend — base URL comes from VITE_AI_SERVICE_URL env var.
 */

import { readToken } from '../../../shared/auth/session';

/** Base URL for the AI service (Python FastAPI) */
const AI_BASE_URL = import.meta.env.VITE_AI_SERVICE_URL || 'http://localhost:8000';

/** Get auth token from sessionStorage */
const getToken = () => readToken();

/** Build Authorization header */
const authHeaders = (extra = {}) => {
  const token = getToken();
  return {
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
    ...extra,
  };
};

/** Generic JSON request helper for the AI service */
const aiRequest = async (path, { method = 'GET', body, headers = {} } = {}) => {
  const url = `${AI_BASE_URL}${path}`;
  const res = await fetch(url, {
    method,
    headers: authHeaders({ 'Content-Type': 'application/json', ...headers }),
    ...(body ? { body: JSON.stringify(body) } : {}),
  });

  if (!res.ok) {
    const data = await res.json().catch(() => ({}));
    const err = new Error(data.error || `HTTP ${res.status}`);
    err.status = res.status;
    err.data = data;
    throw err;
  }

  // 204 No Content — nothing to parse
  if (res.status === 204) return null;

  const ct = res.headers.get('content-type') || '';
  return ct.includes('application/json') ? res.json() : res.text();
};

// ─── Messaging ───────────────────────────────────────────────

/**
 * Send a chat message and consume the SSE stream.
 *
 * @param {Object} params
 * @param {string} params.sessionId
 * @param {string} params.content
 * @param {Object} [params.context] - { page, project_id, api_request_id }
 * @param {Function} params.onToken  - called with each text token
 * @param {Function} [params.onError] - called with error event data
 * @param {Function} [params.onDone]  - called when stream ends
 * @param {Function} [params.onMetadata] - called with context_usage metadata from SSE
 * @param {Function} [params.onRollover] - called when session auto-rolls over (new_session_id, old_session_id, summary_preview)
 * @param {AbortSignal} [params.signal] - optional abort signal
 * @returns {Promise<void>}
 */
export async function sendMessage({ sessionId, content, context, onToken, onError, onDone, onMetadata, onRollover, onFollowups, signal }) {
  const url = `${AI_BASE_URL}/chat/message`;
  const res = await fetch(url, {
    method: 'POST',
    headers: authHeaders({ 'Content-Type': 'application/json' }),
    body: JSON.stringify({ session_id: sessionId, content, context }),
    signal,
  });

  if (!res.ok) {
    const data = await res.json().catch(() => ({}));
    throw new Error(data.error || `HTTP ${res.status}`);
  }

  const reader = res.body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';

  while (true) {
    const { done, value } = await reader.read();
    if (done) break;

    buffer += decoder.decode(value, { stream: true });
    const parts = buffer.split('\n\n');
    buffer = parts.pop() || '';

    for (const part of parts) {
      if (!part.trim()) continue;
      const lines = part.split('\n');
      let eventType = 'message';
      let eventData = '';

      for (const line of lines) {
        if (line.startsWith('event:')) eventType = line.slice(6).trim();
        else if (line.startsWith('data:')) eventData += line.slice(5).trim();
      }

      if (eventType === 'error') {
        try { onError?.(JSON.parse(eventData)); } catch { onError?.({ message: eventData }); }
      } else if (eventType === 'metadata') {
        try { onMetadata?.(JSON.parse(eventData)); } catch { /* ignore */ }
      } else if (eventType === 'context_rollover') {
        try { onRollover?.(JSON.parse(eventData)); } catch { /* ignore */ }
      } else if (eventType === 'done') {
        // Stream complete — don't render as text
        try {
          const parsed = JSON.parse(eventData);
          onDone?.(parsed);
        } catch { onDone?.(); }
      } else if (eventType === 'followups') {
        try { onFollowups?.(JSON.parse(eventData)); } catch { /* ignore */ }
      } else if (eventData) {
        try {
          const parsed = JSON.parse(eventData);
          if (parsed.token !== undefined) {
            onToken?.(parsed.token);
          }
        } catch {
          // Plain text token (fallback)
          onToken?.(eventData);
        }
      }
    }
  }

  // Process remaining buffer
  if (buffer.trim()) {
    const lines = buffer.split('\n');
    let eventData = '';
    for (const line of lines) {
      if (line.startsWith('data:')) eventData += line.slice(5).trim();
    }
    if (eventData) {
      try {
        const parsed = JSON.parse(eventData);
        if (parsed.token !== undefined) onToken?.(parsed.token);
      } catch {
        onToken?.(eventData);
      }
    }
  }

  // onDone already called inside the SSE parser when 'done' event arrives.
  // Do NOT call it again here — that causes a double-fetch and scroll jump.
}

// ─── Sessions ────────────────────────────────────────────────

export const createSession = () =>
  aiRequest('/chat/sessions', { method: 'POST' });

export const listSessions = () =>
  aiRequest('/chat/sessions');

export const getSessionMessages = (sessionId) =>
  aiRequest(`/chat/sessions/${sessionId}/messages`);

export const deleteSession = (sessionId) =>
  aiRequest(`/chat/sessions/${sessionId}`, { method: 'DELETE' });

export const renameSession = (sessionId, title) =>
  aiRequest(`/chat/sessions/${sessionId}`, { method: 'PATCH', body: { title } });

// ─── Feedback ────────────────────────────────────────────────

export const submitFeedback = (messageId, feedback) =>
  aiRequest(`/chat/message/${messageId}/feedback`, { method: 'POST', body: { feedback } });

// ─── Slash Commands ──────────────────────────────────────────

export const getCommands = () =>
  aiRequest('/chat/commands');

// ─── File Upload ─────────────────────────────────────────────

/**
 * Upload a file attachment.
 * @param {File} file
 * @returns {Promise<Object>} upload result
 */
export async function uploadFile(file) {
  const url = `${AI_BASE_URL}/chat/upload`;
  const formData = new FormData();
  formData.append('file', file);

  const res = await fetch(url, {
    method: 'POST',
    headers: authHeaders(), // no Content-Type — browser sets multipart boundary
    body: formData,
  });

  if (!res.ok) {
    const data = await res.json().catch(() => ({}));
    const err = new Error(data.error || `HTTP ${res.status}`);
    err.status = res.status;
    throw err;
  }

  return res.json();
}

// ─── User Preferences ───────────────────────────────────────

export const getAgentPreference = () =>
  aiRequest('/user/agent-preference');

export const saveAgentPreference = (agentType) =>
  aiRequest('/user/agent-preference', { method: 'POST', body: { agent_type: agentType } });

export const updateAgentPreference = (agentType) =>
  aiRequest('/user/agent-preference', { method: 'PATCH', body: { agent_type: agentType } });

export const updateChatbotPosition = (position) =>
  aiRequest('/user/chatbot-position', { method: 'PATCH', body: { position } });

// ─── Default export ──────────────────────────────────────────

const chatbotApi = {
  sendMessage,
  createSession,
  listSessions,
  getSessionMessages,
  deleteSession,
  renameSession,
  submitFeedback,
  getCommands,
  uploadFile,
  getAgentPreference,
  saveAgentPreference,
  updateAgentPreference,
  updateChatbotPosition,
};

export default chatbotApi;
